import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { isBefore } from "date-fns";
import { Box, Paper, Toolbar, Typography } from "@mui/material";
import {
  CustomButton,
  TextHeader,
  TextSubHeader,
  IdentificationFileUpload,
} from "components";
import { useStyles } from "hooks";
import {
  AppRoutes,
  CSSGenerator,
  DocumentStatus,
  DocumentType,
  IdentityFileType,
  IdentityFileData,
} from "interfaces";
import {
  client,
  GetCourseDetailsParams,
  saveIdentityFile,
  saveIdentityFileByUUID,
  useDocumentDataSimplifiedByUUID,
} from "api";
import { AppContext, FeedbackContext } from "contexts";
import {
  fileToBase64,
  generateIdentificationFilePdf,
  compressImage,
} from "utils";

export interface IdentificationFileData {
  front?: File;
  back?: File;
}

export interface IdentificationFilePreview {
  front?: string;
  back?: string;
}

export const IdentificationFileUploadPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const history = useHistory();
  const location = useLocation<
    | {
        refetchCourseDetailsQueryParams?: GetCourseDetailsParams;
        comingFromMassSign?: boolean;
        resumeMassSignId?: number;
      }
    | undefined
  >();
  const { data, loading: loadingData } = useDocumentDataSimplifiedByUUID(
    uuid,
    false,
    location.state?.comingFromMassSign,
  );

  const styles = useStyles(generateStyles);
  const { toggleLoader, showSnackbar } = useContext(FeedbackContext);
  const { user } = useContext(AppContext);

  // Precontract participant uploads his identity file BEFORE signing:
  // after the upload he must continue to the document review and signature.
  // Based on the document (not the logged user): the participant signs via
  // public link, and his identity file is always saved by UUID
  const signAfterUpload = useMemo(
    () =>
      data?.document?.type === DocumentType.PRECONTRACT &&
      data?.document?.status === DocumentStatus.PENDING,
    [data],
  );

  // Participant's identity file already uploaded: re-uploading is optional,
  // he can continue directly to the signature
  const existingIdentityFile =
    data?.precontractData?.participant?.identityFile ?? null;
  const existingIdentityFileExpired = useMemo(
    () =>
      !!existingIdentityFile?.expirationDate &&
      isBefore(new Date(existingIdentityFile.expirationDate), new Date()),
    [existingIdentityFile],
  );
  const [reuploadRequested, setReuploadRequested] = useState(false);
  const identityFileAlreadyUploaded =
    signAfterUpload && !!existingIdentityFile && !reuploadRequested;

  const continueToSign = useCallback(() => {
    history.replace(AppRoutes.SIGN_DOCUMENT.replace(":uuid", uuid));
  }, [history, uuid]);

  const requestReupload = useCallback(() => {
    setReuploadRequested(true);
  }, [setReuploadRequested]);

  const [compressedFiles, setCompressedFiles] =
    useState<IdentificationFileData | null>(null);
  const [processing, setProcessing] = useState(0);
  const [filesContent, setFilesContent] =
    useState<IdentificationFilePreview | null>(null);

  const onFileChange = useCallback(
    async (section: keyof IdentificationFileData, file: File) => {
      if (!file) return;
      // Compress and save file content (base64)
      setProcessing((prev) => prev + 1);
      toggleLoader(
        true,
        `[IdentificationFileUploadPage] Uploading ${section} file`,
      );
      const compressedFile = await compressImage(file);
      setCompressedFiles((prev) => ({
        ...prev,
        [section]: compressedFile,
      }));
      const content = await fileToBase64(compressedFile);
      setFilesContent((prev) => ({
        ...prev,
        [section]: content,
      }));
      setProcessing((prev) => prev - 1);
      toggleLoader(
        false,
        `[IdentificationFileUploadPage] Uploading ${section} file`,
      );
      showSnackbar("Seleccionada correctamente", "success");
    },
    [
      setProcessing,
      setFilesContent,
      setCompressedFiles,
      toggleLoader,
      showSnackbar,
    ],
  );

  const onDeleteFile = useCallback(() => {
    setFilesContent(null);
  }, [setFilesContent]);

  const onFinish = useCallback(
    async (expirationDate: Date | null) => {
      if (
        !filesContent?.front ||
        !filesContent?.back ||
        !compressedFiles?.front ||
        !compressedFiles?.back
      )
        return;
      toggleLoader(true, "[IdentificationFileUploadPage] Uploading file");
      const combinedFile = await generateIdentificationFilePdf({
        frontContent: filesContent.front,
        backContent: filesContent.back,
      });
      if (!combinedFile) return;
      // Upload files
      const filesData: IdentityFileData[] = [
        {
          type: IdentityFileType.COMBINED,
          file: new File([combinedFile], "file_name.pdf", {
            lastModified: new Date().getTime(),
            type: combinedFile.type,
          }),
        },
        {
          type: IdentityFileType.FRONT_FACE,
          file: compressedFiles.front as File,
        },
        {
          type: IdentityFileType.BACK_FACE,
          file: compressedFiles.back as File,
        },
      ];
      const fileUploaded =
        user && !signAfterUpload
          ? await saveIdentityFile(
              {
                files: filesData,
                expirationDate,
              },
              location.state?.refetchCourseDetailsQueryParams,
            )
          : await saveIdentityFileByUUID({
              uuid,
              files: filesData,
            });
      if (!fileUploaded) {
        toggleLoader(false, "[IdentificationFileUploadPage] Uploading file");
        showSnackbar("Algo falló al guardar la cédula de identidad", "error");
        return;
      }
      toggleLoader(false, "[IdentificationFileUploadPage] Uploading file");
      if (uuid && signAfterUpload) {
        // Participant (Precontract, not signed yet): continue to the document
        // review and signature. Evict cached document data so SignPage
        // refetches the recently uploaded identity file.
        client.cache.evict({
          id: "ROOT_QUERY",
          fieldName: "getDocumentDataByUUID",
        });
        client.cache.gc();
        history.replace(AppRoutes.SIGN_DOCUMENT.replace(":uuid", uuid));
      } else if (location.state?.resumeMassSignId !== undefined) {
        history.replace(AppRoutes.PRECONTRACTS, {
          resumeMassSignId: location.state.resumeMassSignId,
        });
      } else if (uuid) {
        // Participant (DJP/Precontract) or OTEC (DJO)
        history.replace(`${AppRoutes.SUCCESSFUL_SIGNATURE}/${uuid}`);
      } else {
        // Representante Legal Empresa (mass precontract sign)
        history.replace(AppRoutes.PRECONTRACTS);
      }
    },
    [
      filesContent,
      compressedFiles,
      history,
      location,
      showSnackbar,
      signAfterUpload,
      toggleLoader,
      user,
      uuid,
    ],
  );

  useEffect(() => {
    toggleLoader(
      loadingData,
      "[IdentificationFileUploadPage] Fetching document data",
    );
  }, [loadingData, toggleLoader]);
  useEffect(() => {
    // Wait for document data: the participant identity file upload (by UUID)
    // must not be skipped because the logged user has his own identity file
    if (loadingData || signAfterUpload) return;
    if (user?.identityFile?.url) {
      history.replace(AppRoutes.DASHBOARD);
    }
  }, [user?.identityFile?.url, history, loadingData, signAfterUpload]);

  return (
    <Box sx={styles.root}>
      <Toolbar />
      <TextHeader text={"Cédula de identidad"} showAvatar={false} />
      <Paper sx={styles.containerHeader} elevation={4}>
        <TextSubHeader
          text={
            identityFileAlreadyUploaded
              ? "Tu cédula de identidad ya fue cargada"
              : `Sube tu cédula de identidad ${
                  signAfterUpload
                    ? "para continuar con la firma"
                    : !user
                    ? "para finalizar el proceso de firma"
                    : ""
                }`
          }
        />
      </Paper>
      <Box sx={styles.content}>
        {identityFileAlreadyUploaded ? (
          <Paper sx={styles.alreadyUploadedCard} elevation={4}>
            <Typography sx={styles.alreadyUploadedTitle}>
              {existingIdentityFileExpired
                ? "Tu cédula de identidad ya fue cargada, pero está vencida"
                : "Tu cédula de identidad ya fue cargada"}
            </Typography>
            <Typography sx={styles.alreadyUploadedDescription}>
              {existingIdentityFileExpired
                ? "Te recomendamos volver a cargarla, pero no es obligatorio: puedes continuar directamente con la firma del documento."
                : "No necesitas volver a cargarla: puedes continuar directamente con la firma del documento. Si lo prefieres, puedes cargarla nuevamente."}
            </Typography>
            <Box sx={styles.alreadyUploadedActions}>
              <CustomButton variant="contained" onClick={continueToSign}>
                CONTINUAR A LA FIRMA
              </CustomButton>
              <CustomButton variant="outlined" onClick={requestReupload}>
                VOLVER A CARGAR CÉDULA
              </CustomButton>
            </Box>
          </Paper>
        ) : (
          <IdentificationFileUpload
            filePreview={filesContent}
            minExpirationDate={data?.courseLiquidationDate ?? null}
            onFileChange={onFileChange}
            onDeleteFile={onDeleteFile}
            onFileUpload={onFinish}
            processing={!!processing}
          />
        )}
      </Box>
    </Box>
  );
};

const generateStyles: CSSGenerator = (theme) => ({
  root: { height: "100vh" },
  content: {
    height: "calc(100vh - 128px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  alreadyUploadedCard: {
    maxWidth: "480px",
    margin: "0 32px",
    padding: "32px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    textAlign: "center",
  },
  alreadyUploadedTitle: {
    fontSize: "18px",
    fontWeight: "bold",
  },
  alreadyUploadedDescription: {
    color: theme.palette.text.secondary,
  },
  alreadyUploadedActions: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginTop: "8px",
  },
});
